// WHICH source an open-ended answer's uploads belong to.
//
// The open-ended material content does more than render a textarea: it uploads a
// photo / PDF / drawing / recording to the learner's profile storage, and every one
// of those calls needs a (sourceType, sourceId) pair — it is the upsert key on
// /storage-files/user-profile/upload/file, the `type`/`applicationId` in the phone
// QR payload, and the audio file's name prefix.
//
// That pair used to be reconstructed inline, in four places, from two SPECIFIC id
// props (`branchAssignmentId` / `personalizedAssignmentId`). Any surface that isn't
// one of those two assignment kinds therefore resolved to NO source — which is what
// left support assignments rendering all three upload tiles while every click only
// toasted "Önizleme modunda işlem yapılamaz".
//
// So the pair is resolved ONCE, here, and a caller that is neither of the two legacy
// kinds simply passes the pair explicitly (`sourceType="SUPPORT_ASSIGNMENT"` +
// `sourceId={supportAssignmentId}`) instead of the component growing a third id prop
// per source kind.
//
// Absence is `null`, NOT an empty pair: `null` is what keeps a genuinely source-less
// surface (the library's ActivityPreviewByData, the /practise review screens) inert —
// exactly as it is today.

export interface OpenEndedSourceInput {
  /** Explicit source kind — wins over the legacy id props when paired with a sourceId. */
  sourceType?: string | null;
  /** Explicit source id — the id of the `sourceType` entity. */
  sourceId?: any;
  /** Legacy: a corporate assignment ⇒ BRANCH_ASSIGNMENT. */
  branchAssignmentId?: any;
  /** Legacy: a personalized assignment ⇒ PERSONALIZED_ASSIGNMENT. */
  personalizedAssignmentId?: any;
}

export interface OpenEndedSource {
  sourceType: string;
  sourceId: any;
  /**
   * Kebab-cased sourceType + id — the recording file-name prefix. Reproduces the
   * strings the legacy inline code built by hand (`branch-assignment-12`,
   * `personalized-assignment-12`), so existing uploads keep their names.
   */
  slug: string;
}

/**
 * Resolve the (sourceType, sourceId) pair for an open-ended answer's uploads.
 *
 * Precedence: an explicit pair → BRANCH_ASSIGNMENT → PERSONALIZED_ASSIGNMENT → null.
 *
 * Presence is TRUTHINESS, not `!= null`, deliberately: it is what the inline code it
 * replaces used (`if (branchAssignmentId)`), and callers really do pass
 * `branchAssignmentId={null}` / `""` to mean "not this kind" — see
 * PersonalizedAssignmentScreen. No id in this system is legitimately `0`.
 */
export const resolveOpenEndedSource = (
  input: OpenEndedSourceInput | null | undefined,
): OpenEndedSource | null => {
  const {
    sourceType,
    sourceId,
    branchAssignmentId,
    personalizedAssignmentId,
  } = input || {};

  if (sourceType && sourceId) {
    return { sourceType, sourceId, slug: buildSlug(sourceType, sourceId) };
  }
  if (branchAssignmentId) {
    return {
      sourceType: "BRANCH_ASSIGNMENT",
      sourceId: branchAssignmentId,
      slug: buildSlug("BRANCH_ASSIGNMENT", branchAssignmentId),
    };
  }
  if (personalizedAssignmentId) {
    return {
      sourceType: "PERSONALIZED_ASSIGNMENT",
      sourceId: personalizedAssignmentId,
      slug: buildSlug("PERSONALIZED_ASSIGNMENT", personalizedAssignmentId),
    };
  }
  return null;
};

const buildSlug = (sourceType: string, sourceId: any): string =>
  `${sourceType.toLowerCase().replace(/_/g, "-")}-${sourceId}`;
